/**
 * ProductPageHeader — filtro, busca, criar, import/export.
 * Mobile: ferramentas → busca → CTA. Desktop intacto.
 */
import React, { useRef } from 'react';
import SearchInput from '../../../components/ui/SearchInput';
import { AppIcon } from '../../../components/icons/AppIcon';
import ActionMenu, { ActionMenuItem } from '../../../components/shared/ActionMenu';
import { Button } from '../../../components/ui/Button';
import { cn } from '@/lib/utils';

interface ProductPageHeaderProps {
    onAddProduct: () => void;
    onExport: () => void;
    onImport: (file: File) => void;
    isImporting?: boolean;
    searchValue: string;
    onSearchChange: (value: string) => void;
    onOpenFilters: () => void;
    hasActiveFilters?: boolean;
    onRefresh?: () => void;
    isRefreshing?: boolean;
}

const ProductPageHeader: React.FC<ProductPageHeaderProps> = ({
    onAddProduct,
    onExport,
    onImport,
    isImporting = false,
    searchValue,
    onSearchChange,
    onOpenFilters,
    hasActiveFilters = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const menuActions: ActionMenuItem[] = [
        {
            label: 'Importar',
            icon: <AppIcon name="upload" className="h-4 w-4" />,
            onClick: () => fileInputRef.current?.click(),
            disabled: isImporting,
        },
        {
            label: 'Exportar .xls',
            icon: <AppIcon name="download" className="h-4 w-4" />,
            onClick: onExport,
        },
    ];

    const filterButton = (embedded: boolean) => (
        <button
            type="button"
            onClick={onOpenFilters}
            title="Filtros"
            aria-label="Abrir filtros"
            aria-pressed={hasActiveFilters}
            className={cn(
                'group flex shrink-0 items-center justify-center touch-manipulation transition-colors',
                embedded ? 'h-11 w-11 rounded-none border-0' : 'h-9 w-9 rounded-lg border',
                hasActiveFilters
                    ? embedded
                        ? 'bg-primary-subtle text-primary'
                        : 'border-primary-light bg-primary-subtle text-primary'
                    : embedded
                        ? 'bg-transparent text-default hover:bg-surface-subtle hover:text-primary'
                        : 'border-border bg-card text-secondary hover:bg-surface-subtle hover:text-primary',
            )}
        >
            <AppIcon name="filter" className="h-5 w-5" aria-hidden />
        </button>
    );

    return (
        <>
            <div className="flex w-full flex-col gap-s3 md:hidden">
                <div
                    role="toolbar"
                    aria-label="Filtros e ações de produtos"
                    className="inline-flex h-11 w-fit items-stretch overflow-hidden rounded-xl border border-border bg-card divide-x divide-border"
                >
                    {filterButton(true)}
                    <ActionMenu
                        actions={menuActions}
                        buttonClassName="h-11 w-11 rounded-none border-0 bg-transparent p-0 text-default shadow-none hover:bg-surface-subtle hover:text-primary"
                    />
                </div>

                <SearchInput
                    value={searchValue}
                    onChange={onSearchChange}
                    placeholder="Buscar produtos..."
                    className="min-h-11 w-full"
                />

                <Button
                    type="button"
                    onClick={onAddProduct}
                    className="min-h-11 w-full touch-manipulation"
                >
                    Novo Produto
                </Button>
            </div>

            <div className="hidden md:flex md:flex-row md:items-center md:gap-2.5">
                {filterButton(false)}

                <div className="w-[200px] shrink-0">
                    <SearchInput
                        value={searchValue}
                        onChange={onSearchChange}
                        placeholder="Pesquisar produtos..."
                        className="h-[36px]"
                    />
                </div>

                <Button type="button" onClick={onAddProduct} className="whitespace-nowrap">
                    Novo Produto
                </Button>

                <ActionMenu actions={menuActions} />
            </div>

            <input
                ref={fileInputRef}
                id="product-import-file"
                name="product-import-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                        onImport(file);
                        event.target.value = '';
                    }
                }}
                className="hidden"
                disabled={isImporting}
            />
        </>
    );
};

export default ProductPageHeader;
